import React, { useState, useEffect } from 'react';
import { X, Zap, Settings } from 'lucide-react';

export default function MonthlyDataForm({ record, onSave, onCancel, userCurrency = 'USD' }) {
  const [entryMode, setEntryMode] = useState('basic'); // 'basic' or 'advanced'
  const [formData, setFormData] = useState({
    trackingMonth: '',
    entryMode: 'basic',
    // Basic mode fields
    totalSales: 0,
    totalClients: 0,
    // Advanced mode fields
    existingClientSales: 0,
    newClientSales: 0,
    existingClientRevenue: 0,
    newClientRevenue: 0,
    averageSaleAmount: 0,
    grossProfitPercentage: 50,
    googleAdsExpense: 0,
    otherMonthlyCosts: 0,
    currency: userCurrency,
    notes: ''
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (record) {
      const mode = record?.entryMode || 'advanced';
      setEntryMode(mode);
      
      setFormData({
        trackingMonth: record?.trackingMonth?.substring(0, 7) || '',
        entryMode: mode,
        totalSales: record?.totalSales || 0,
        totalClients: (record?.existingClientSales || 0) + (record?.newClientSales || 0),
        existingClientSales: record?.existingClientSales || 0,
        newClientSales: record?.newClientSales || 0,
        existingClientRevenue: record?.existingClientRevenue || 0,
        newClientRevenue: record?.newClientRevenue || 0,
        averageSaleAmount: record?.averageSaleAmount || 0,
        grossProfitPercentage: record?.grossProfitPercentage || 50,
        googleAdsExpense: record?.googleAdsExpense || 0,
        otherMonthlyCosts: record?.otherMonthlyCosts || 0,
        currency: record?.currency || userCurrency,
        notes: record?.notes || ''
      });
    } else {
      setFormData(prev => ({
        ...prev,
        currency: userCurrency,
        entryMode: 'basic'
      }));
    }
  }, [record, userCurrency]);

  const handleModeToggle = (mode) => {
    setEntryMode(mode);
    setFormData(prev => ({
      ...prev,
      entryMode: mode
    }));
  };

  const handleChange = (e) => {
    const { name, value, type } = e?.target;
    const numValue = type === 'number' ? parseFloat(value) || 0 : value;
    
    setFormData(prev => {
      const updated = {
        ...prev,
        [name]: numValue
      };

      // Auto-calculate in basic mode
      if (entryMode === 'basic') {
        if (name === 'totalSales' || name === 'totalClients') {
          // When total clients changes, distribute evenly for calculations
          if (name === 'totalClients') {
            updated.existingClientSales = Math.floor(numValue * 0.6); // 60% existing
            updated.newClientSales = numValue - updated?.existingClientSales;
          }
        }
      }

      return updated;
    });
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    setError('');
    setLoading(true);

    try {
      if (!formData?.trackingMonth) {
        throw new Error('Please select a month');
      }

      // Prepare data based on entry mode
      let submitData = {
        ...formData,
        trackingMonth: formData?.trackingMonth,
        entryMode: entryMode
      };

      // In basic mode, use simplified fields
      if (entryMode === 'basic') {
        submitData.totalSales = formData?.totalSales;
        // Split total clients into existing and new (60/40 split if not specified)
        const totalClients = formData?.totalClients || 0;
        submitData.existingClientSales = Math.floor(totalClients * 0.6);
        submitData.newClientSales = totalClients - submitData?.existingClientSales;
      }

      await onSave(submitData);
    } catch (err) {
      setError(err?.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex justify-between items-center p-6 border-b sticky top-0 bg-white z-10">
          <h2 className="text-xl font-bold text-gray-900">
            {record ? 'Edit Monthly Data' : 'Add Monthly Data'}
          </h2>
          <button onClick={onCancel} className="text-gray-400 hover:text-gray-600">
            <X size={24} />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {error && (
            <div className="p-4 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
              {error}
            </div>
          )}

          {/* Entry Mode Toggle */}
          <div className="bg-gray-50 p-4 rounded-lg">
            <label className="block text-sm font-medium text-gray-700 mb-3">
              Entry Mode
            </label>
            <div className="flex gap-3">
              <button
                type="button"
                onClick={() => handleModeToggle('basic')}
                className={`flex-1 flex items-center justify-center gap-2 px-4 py-3 rounded-lg font-medium transition-all ${
                  entryMode === 'basic' ?'bg-blue-600 text-white shadow-md' :'bg-white text-gray-700 border border-gray-300 hover:border-blue-400'
                }`}
              >
                <Zap size={18} />
                <span>Basic Mode</span>
              </button>
              <button
                type="button"
                onClick={() => handleModeToggle('advanced')}
                className={`flex-1 flex items-center justify-center gap-2 px-4 py-3 rounded-lg font-medium transition-all ${
                  entryMode === 'advanced' ?'bg-blue-600 text-white shadow-md' :'bg-white text-gray-700 border border-gray-300 hover:border-blue-400'
                }`}
              >
                <Settings size={18} />
                <span>Advanced Mode</span>
              </button>
            </div>
            <p className="text-xs text-gray-500 mt-2">
              {entryMode === 'basic' ?'Quick entry with automatic calculations for total sales and clients' :'Detailed entry with full control over all financial metrics'}
            </p>
          </div>

          {/* Month Selection */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Tracking Month *
            </label>
            <input
              type="month"
              name="trackingMonth"
              value={formData?.trackingMonth}
              onChange={handleChange}
              required
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>

          {/* Basic Mode Fields */}
          {entryMode === 'basic' && (
            <div className="space-y-4">
              <h3 className="text-lg font-semibold text-gray-900">Basic Information</h3>
              
              {/* Revenue & Clients */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Total Revenue
                  </label>
                  <input
                    type="number"
                    name="totalSales"
                    value={formData?.totalSales}
                    onChange={handleChange}
                    min="0"
                    placeholder="Total revenue amount"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                  <p className="text-xs text-gray-500 mt-1">Total sales revenue for the month</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Total Clients/Orders
                  </label>
                  <input
                    type="number"
                    name="totalClients"
                    value={formData?.totalClients}
                    onChange={handleChange}
                    min="0"
                    placeholder="Number of clients or orders"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                  <p className="text-xs text-gray-500 mt-1">Total number of clients or orders</p>
                </div>
              </div>

              {/* Average Profit Margin */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Average Profit Margin (%)
                </label>
                <input
                  type="number"
                  name="grossProfitPercentage"
                  value={formData?.grossProfitPercentage}
                  onChange={handleChange}
                  min="0"
                  max="100"
                  step="0.01"
                  placeholder="Enter average profit margin percentage"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
                <p className="text-xs text-gray-500 mt-1">Your average profit margin as a percentage</p>
              </div>

              {/* Agency Fee & PPC Fees */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Agency Fee
                  </label>
                  <input
                    type="number"
                    name="otherMonthlyCosts"
                    value={formData?.otherMonthlyCosts}
                    onChange={handleChange}
                    min="0"
                    step="0.01"
                    placeholder="Enter agency fee amount"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                  <p className="text-xs text-gray-500 mt-1">Monthly agency management fee</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    PPC Fees
                  </label>
                  <input
                    type="number"
                    name="googleAdsExpense"
                    value={formData?.googleAdsExpense}
                    onChange={handleChange}
                    min="0"
                    step="0.01"
                    placeholder="Enter PPC advertising fees"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                  <p className="text-xs text-gray-500 mt-1">Pay-per-click advertising costs</p>
                </div>
              </div>

              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                <p className="text-sm text-blue-800">
                  <strong>Basic Mode:</strong> Enter your total revenue, clients/orders, average profit margin, agency fee, and PPC fees. 
                  We'll automatically calculate detailed metrics and monthly net revenue.
                </p>
              </div>
            </div>
          )}

          {/* Advanced Mode Fields */}
          {entryMode === 'advanced' && (
            <>
              {/* Sales Data */}
              <div className="space-y-4">
                <h3 className="text-lg font-semibold text-gray-900">Sales Information</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Existing Client Sales/Orders
                    </label>
                    <input
                      type="number"
                      name="existingClientSales"
                      value={formData?.existingClientSales}
                      onChange={handleChange}
                      min="0"
                      placeholder="Number of existing client sales"
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                    <p className="text-xs text-gray-500 mt-1">Total sales from existing clients</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      New Client Sales/Orders
                    </label>
                    <input
                      type="number"
                      name="newClientSales"
                      value={formData?.newClientSales}
                      onChange={handleChange}
                      min="0"
                      placeholder="Number of new client sales"
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                    <p className="text-xs text-gray-500 mt-1">Total sales from new clients</p>
                  </div>
                </div>
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mt-3">
                  <p className="text-sm text-blue-800">
                    Total is automatically calculated: {(formData?.existingClientSales || 0) + (formData?.newClientSales || 0)} sales/orders
                  </p>
                </div>
              </div>

              {/* Financial Metrics */}
              <div className="space-y-4">
                <h3 className="text-lg font-semibold text-gray-900">Financial Details</h3>
                
                {/* Revenue Breakdown */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Existing Client Revenue
                    </label>
                    <input
                      type="number"
                      name="existingClientRevenue"
                      value={formData?.existingClientRevenue}
                      onChange={handleChange}
                      min="0"
                      step="0.01"
                      placeholder="Revenue from existing clients"
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                    <p className="text-xs text-gray-500 mt-1">Revenue generated by existing clients</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      New Client Revenue
                    </label>
                    <input
                      type="number"
                      name="newClientRevenue"
                      value={formData?.newClientRevenue}
                      onChange={handleChange}
                      min="0"
                      step="0.01"
                      placeholder="Revenue from new clients"
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                    <p className="text-xs text-gray-500 mt-1">Revenue generated by new clients</p>
                  </div>
                </div>

                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                  <p className="text-sm text-blue-800">
                    Total Revenue: {new Intl.NumberFormat('en-US', { 
                      style: 'currency', 
                      currency: formData?.currency || 'USD' 
                    })?.format((formData?.existingClientRevenue || 0) + (formData?.newClientRevenue || 0))}
                  </p>
                </div>

                {/* Profit Margin */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Profit Margin (%)
                  </label>
                  <input
                    type="number"
                    name="grossProfitPercentage"
                    value={formData?.grossProfitPercentage}
                    onChange={handleChange}
                    min="0"
                    max="100"
                    step="0.01"
                    placeholder="Enter profit margin percentage"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                  <p className="text-xs text-gray-500 mt-1">Your profit margin as a percentage</p>
                </div>

                {/* Agency Fee & PPC Fees */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Agency Fee
                    </label>
                    <input
                      type="number"
                      name="otherMonthlyCosts"
                      value={formData?.otherMonthlyCosts}
                      onChange={handleChange}
                      min="0"
                      step="0.01"
                      placeholder="Enter agency fee amount"
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                    <p className="text-xs text-gray-500 mt-1">Monthly agency management fee</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      PPC Fees
                    </label>
                    <input
                      type="number"
                      name="googleAdsExpense"
                      value={formData?.googleAdsExpense}
                      onChange={handleChange}
                      min="0"
                      step="0.01"
                      placeholder="Enter PPC advertising fees"
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                    <p className="text-xs text-gray-500 mt-1">Pay-per-click advertising costs</p>
                  </div>
                </div>
              </div>
            </>
          )}

          {/* Notes (Available in both modes) */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Notes (Optional)
            </label>
            <textarea
              name="notes"
              value={formData?.notes}
              onChange={handleChange}
              rows="3"
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              placeholder="Add any additional notes about this month..."
            />
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-3 pt-4 border-t">
            <button
              type="button"
              onClick={onCancel}
              className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {loading ? 'Saving...' : record ? 'Update' : 'Save'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}